import React, { useMemo, useRef, useState } from 'react';
import { ColDef, ColumnState, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { FeederInfoDto } from 'src/containers/PDA/PDA1120_R01/PDA1120_R01Dto';
import {
  aggridHeaderClass,
  pdaCellStyle,
} from 'src/components/common/common';
import usePDA1120_R01Store from './usePDA1120_R01Store';
import { resolveFeeder } from './feederUtils';

interface PDA1120_R01FeederTableProps {
  feederList: FeederInfoDto[];
  onSelectFeeder: (feeder: FeederInfoDto) => void;
}

type FeederInfoDtoWithLocal = FeederInfoDto & {
  inserted?: boolean;
  dbQty?: number;
  localQty?: number;
  /** 현재 공정 기준 피더코드 (행별 구분용) */
  feederCdDisplay?: string;
};

const PDA1120_R01FeederTable: React.FC<PDA1120_R01FeederTableProps> = ({
  feederList,
  onSelectFeeder,
}) => {
  const { localCheckedQty, selectedOrder } = usePDA1120_R01Store();
  const workplaceCd = selectedOrder?.workplaceCd ?? null;
  const gridRef = useRef<AgGridReact<FeederInfoDtoWithLocal>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  /**
   * localCheckedQty 키: resolveFeeder(feeder, workplaceCd)_childItemCd (복합키)
   * 실장된 행(localQty > 0)을 맨 위로 정렬
   */
  const displayFeederList = useMemo(() => {
    const mapped = feederList.map((feeder) => {
      const dbQty = Number(feeder.lotQty) || 0;
      const feederCd = resolveFeeder(feeder, workplaceCd);
      const feederKey = `${feederCd}_${feeder.childItemCd}`;
      const localQty = localCheckedQty[feederKey] || 0;
      const totalQty = dbQty + localQty;

      return {
        ...feeder,
        lotQty: totalQty,
        dbQty,
        localQty,
        checkStatus: totalQty > 0 ? 'Y' : 'N',
        feederCdDisplay: feederCd || '-',
      } as FeederInfoDtoWithLocal;
    });
    return mapped.sort((a, b) => {
      const aMounted = (a.localQty || 0) > 0;
      const bMounted = (b.localQty || 0) > 0;
      if (aMounted && !bMounted) return -1;
      if (!aMounted && bMounted) return 1;
      return 0;
    });
  }, [feederList, localCheckedQty, workplaceCd]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: '피더',
        field: 'feederCdDisplay',
        valueGetter: (params) => params.data?.feederCdDisplay ?? '-',
        cellStyle: (params) => {
          const hasLocalQty = (params.data?.localQty || 0) > 0;
          return {
            textAlign: 'center',
            ...pdaCellStyle,
            justifyContent: 'center',
            backgroundColor: hasLocalQty ? '#fff3cd' : 'transparent',
            fontWeight: hasLocalQty || params.data?.checkStatus === 'Y' ? 'bold' : 'normal',
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
      {
        headerName: '품명',
        field: 'childItemNm',
        cellStyle: (params) => {
          const hasLocalQty = (params.data?.localQty || 0) > 0;
          return {
            textAlign: 'left',
            ...pdaCellStyle,
            backgroundColor: hasLocalQty ? '#fff3cd' : 'transparent',
            fontWeight: hasLocalQty || params.data?.checkStatus === 'Y' ? 'bold' : 'normal',
          };
        },
        headerClass: aggridHeaderClass,
        flex: 3,
        minWidth: 100,
      },
      {
        headerName: '필요량',
        field: 'childItemUsage',
        valueGetter: (params) =>
          params.data?.childItemUsage != null
            ? params.data.childItemUsage.toLocaleString()
            : '-',
        cellStyle: (params) => {
          const hasLocalQty = (params.data?.localQty || 0) > 0;
          return {
            textAlign: 'right',
            ...pdaCellStyle,
            justifyContent: 'flex-end',
            backgroundColor: hasLocalQty ? '#fff3cd' : 'transparent',
            fontWeight: hasLocalQty || params.data?.checkStatus === 'Y' ? 'bold' : 'normal',
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
      {
        headerName: '실장량',
        field: 'lotQty',
        valueGetter: (params) =>
          params.data?.lotQty != null ? params.data.lotQty.toLocaleString() : '-',
        cellStyle: (params) => {
          const hasLocalQty = (params.data?.localQty || 0) > 0;
          return {
            textAlign: 'right',
            ...pdaCellStyle,
            justifyContent: 'flex-end',
            backgroundColor: hasLocalQty ? '#fff3cd' : 'transparent',
            fontWeight: hasLocalQty || params.data?.checkStatus === 'Y' ? 'bold' : 'normal',
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
    ] as ColDef<FeederInfoDtoWithLocal>[],
    []
  );

  const handleRowClicked = (event: RowClickedEvent<FeederInfoDtoWithLocal>) => {
    if (event.data) onSelectFeeder(event.data);
  };

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={displayFeederList}
        initSelected={() => {}}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1120_R01_FeederTable_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<FeederInfoDtoWithLocal>
            ref={gridRef}
            rowData={displayFeederList}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1120_R01_FeederTable_ColumnState"
            setColumnState={setColumnState}
            onRowClicked={handleRowClicked}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1120_R01FeederTable;
